import { useSelector } from 'react-redux';
import { selectBuildersLocations } from '../../../../../selectors/locations';
import { useMemo } from 'react';
import { ILocationsColumn, ILocationsGroup } from '../../../../../components/Filters';

const FAVORITES_LOCATIONS = new Map([
  [4593, 'Москва и МО'],
  [4588, 'Санкт-Петербург и ЛО'],
]);

const COLUMNS_COUNT = 4;

// Отношение высоты заголовка группы к элементу группы
const TITLE_HEIGHT_RATIO = 2.5;

export const useLocations = () => {
  const locations = useSelector(selectBuildersLocations);

  const favorites = useMemo(() => {
    return locations
      .filter(({ id }) => FAVORITES_LOCATIONS.has(id))
      .map(({ id }) => ({
        id,
        name: FAVORITES_LOCATIONS.get(id) as string,
      }))
      .sort((a, b) => b.id - a.id);
  }, [locations]);

  /**
   * Группирует локации по первой букве в названии
   */
  const groups: ILocationsGroup[] = useMemo(() => {
    return Array.from(
      locations
        .reduce<Map<string, ILocationsGroup>>((locationsMap, location) => {
          const key = location.name[0].toUpperCase();
          const value = locationsMap.get(key);

          if (value) {
            value.locations.push(location);
          } else {
            locationsMap.set(key, { title: key, locations: [location] });
          }

          return locationsMap;
        }, new Map())
        .values(),
    );
  }, [locations]);

  /**
   * Равномерно распределяет группы локаций по колонкам
   */
  const columns = useMemo(() => {
    const totalHeight = groups.reduce(
      (sum: number, group: ILocationsGroup) => sum + TITLE_HEIGHT_RATIO + group.locations.length,
      0,
    );

    const targetHeight = totalHeight / COLUMNS_COUNT;
    let colHeight = 0;

    return groups.reduce((cols: ILocationsColumn[], group: ILocationsGroup) => {
      if (!cols.length) {
        cols.push([]);
      }

      let currentCol = cols[cols.length - 1];
      const nextGroupHeight = group.locations.length + TITLE_HEIGHT_RATIO;

      if (currentCol.length === 0) {
        currentCol.push(group);
        colHeight += nextGroupHeight;

        return cols;
      }

      const futureHeight = colHeight + nextGroupHeight;

      if (targetHeight - futureHeight > 0) {
        currentCol.push(group);
        colHeight += nextGroupHeight;

        return cols;
      }

      if (cols.length === COLUMNS_COUNT - 1) {
        currentCol.push(group);
        cols.push([]);
        colHeight = 0;

        return cols;
      }

      if (cols.length === COLUMNS_COUNT) {
        currentCol.push(group);
        colHeight = nextGroupHeight;

        return cols;
      }

      if ((targetHeight - futureHeight) * -1 >= targetHeight - colHeight) {
        cols.push([]);
        currentCol = cols[cols.length - 1];
        currentCol.push(group);
        colHeight = nextGroupHeight;
      } else {
        currentCol = cols[cols.length - 1];
        currentCol.push(group);
        cols.push([]);
        colHeight = 0;
      }

      return cols;
    }, []);
  }, [groups]);

  return { favorites, columns };
};
